/**
 * The reseller's own billing (TMMRSL-0046; TMMAPI-0098 card and charges, TMMAPI-0100 standing
 * emails), all bearer-only under `/v1/reseller-admin` — no cookies, no CSRF:
 *
 * - `GET /billing/card` — the card on file, or a literal JSON `null` (typed by the generated contract).
 * - `PUT /billing/card` (OWNER) — `CardPutRequest { cardToken }`; the idempotency key is derived on
 *   the server. `200 { status: "requires_action", clientAction }` is 3-D Secure: run
 *   `handleNextAction`, then send the SAME body again for the saved card. Replacing is a plain PUT
 *   of the new card (saved first, then the old one removed).
 * - `DELETE /billing/card` (OWNER) — `204`, or `409 RESELLER_CHARGE_UNPAID` while a charge is unpaid.
 * - `GET /billing/charges` — the charges to the card, newest first. The reseller has no retry route.
 * - `GET /notices` — the standing emails sent to the reseller, newest first; recipients are a
 *   count only.
 * - `GET /billing/collection-authorization` — the collection state. `POST` (OWNER) accepts the
 *   served `termsVersion`. `DELETE` (OWNER) withdraws it. No card data.
 *
 * The `cardToken` is never logged or stored; it lives only for the PUT (and its replay).
 */
import type {
  CardPutRequest,
  ClientAction,
  CollectionAuthorizationRequest,
  CollectionAuthorizationView,
  ResellerBillingCard,
  ResellerCharge,
  ResellerChargeList,
  ResellerNoticeCharge,
  ResellerNoticeList,
  ResellerNoticeView,
} from '@/api/generated';

export type { CollectionAuthorizationView };
import { request } from './client';
import { ApiError, backendCode } from './errors';
import { asChargeStatus, asOptionalString, type ChargeStatus } from './resellerAdmin';

export type CardOnFileStatus = 'ACTIVE' | 'EXPIRED' | 'requires_action';

export interface CardOnFile {
  brand: string;
  last4: string;
  expMonth: number | null;
  expYear: number | null;
  status: CardOnFileStatus | null;
}

export type CardPutResult =
  { kind: 'saved'; card: CardOnFile } | { kind: 'requiresAction'; clientAction: ClientAction };

export type ChargeKind = 'MINIMUM_SHORTFALL' | 'STATEMENT_DEFICIT';

/** A charge to the reseller's card — a month under the minimum, or a month with a negative net. */
export interface ResellerChargeItem {
  id: number;
  kind: ChargeKind | null;
  status: ChargeStatus | null;
  amountMinor: number;
  currency: string;
  attempt: number;
  createdAt: string;
  nextRetryAt: string | null;
  /** The month charged for (a civil date, the 1st). */
  periodStart: string;
  /** `NO_CARD`, `REVERIFY_CARD`, or another code — never shown as the message. */
  failureCode: string | null;
  holdsPayouts: boolean;
}

export type NoticeKind =
  | 'LAUNCH_REMINDER'
  | 'PROBATION_STARTED'
  | 'PROBATION_MONTHLY'
  | 'CHARGE_FAILED'
  | 'CHARGE_ACTION_REQUIRED'
  | 'DEACTIVATED'
  | 'RECOVERED';

export type NoticeStatus = 'PENDING' | 'SENDING' | 'SENT' | 'SKIPPED' | 'FAILED';

export interface NoticeCharge {
  currency: string;
  amountMinor: number;
  status: ChargeStatus | null;
}

/** A standing email. Each fact is set only where it applies to the email's kind. */
export interface ResellerNotice {
  id: number;
  kind: NoticeKind | null;
  status: NoticeStatus | null;
  periodOn: string;
  recipientCount: number | null;
  createdAt: string;
  sentAt: string | null;
  facts: {
    month: string | null;
    currency: string;
    shortfallMinor: number | null;
    probationMonths: number | null;
    stillNeededMinor: number | null;
    deactivationOn: string | null;
    launchOn: string | null;
    charges: NoticeCharge[];
  };
}

const CARD_STATUSES: ReadonlySet<string> = new Set<CardOnFileStatus>([
  'ACTIVE',
  'EXPIRED',
  'requires_action',
]);

const CHARGE_KINDS: ReadonlySet<string> = new Set<ChargeKind>([
  'MINIMUM_SHORTFALL',
  'STATEMENT_DEFICIT',
]);

const NOTICE_KINDS: ReadonlySet<string> = new Set<NoticeKind>([
  'LAUNCH_REMINDER',
  'PROBATION_STARTED',
  'PROBATION_MONTHLY',
  'CHARGE_FAILED',
  'CHARGE_ACTION_REQUIRED',
  'DEACTIVATED',
  'RECOVERED',
]);

const NOTICE_STATUSES: ReadonlySet<string> = new Set<NoticeStatus>([
  'PENDING',
  'SENDING',
  'SENT',
  'SKIPPED',
  'FAILED',
]);

/** `value` when it is one of `known`, else `null`. */
function asEnum<T extends string>(value: unknown, known: ReadonlySet<string>): T | null {
  return typeof value === 'string' && known.has(value) ? (value as T) : null;
}

export type CollectionPresentation = 'authorized' | 'lapsed' | 'notAuthorized';

/**
 * The three screens the console shows. Lapsed is a reversal of the consent itself
 * (`reauthorizationRequired` and `revocationReason` `COLLECTION_REVERSED`). The payee's
 * `negativeBalance` is not part of that. `CARD_REPLACED` is not lapsed; the section words it
 * on its own. `ACTIVE` is authorized. Anything else, including `NONE` and the other revocation
 * reasons, is not authorized.
 */
export function collectionPresentation(view: CollectionAuthorizationView): CollectionPresentation {
  if (view.reauthorizationRequired === true && view.revocationReason === 'COLLECTION_REVERSED') {
    return 'lapsed';
  }
  if (view.status === 'ACTIVE') return 'authorized';
  return 'notAuthorized';
}

export function normalizeCollection(body: unknown): CollectionAuthorizationView {
  const raw = body as CollectionAuthorizationView;
  const view: CollectionAuthorizationView = {
    status: raw.status,
    termsVersion: raw.termsVersion,
    reauthorizationRequired: raw.reauthorizationRequired,
  };
  const negativeBalance = asOptionalString(raw.negativeBalance);
  if (negativeBalance !== undefined) view.negativeBalance = negativeBalance;
  const revocationReason = asOptionalString(raw.revocationReason);
  if (revocationReason !== undefined) view.revocationReason = revocationReason;
  const version = asOptionalString(raw.version);
  if (version !== undefined) view.version = version;
  const acceptedAt = asOptionalString(raw.acceptedAt);
  if (acceptedAt !== undefined) view.acceptedAt = acceptedAt;
  const revokedAt = asOptionalString(raw.revokedAt);
  if (revokedAt !== undefined) view.revokedAt = revokedAt;
  return view;
}

/** The card on file, or `null` when the body is not an object or carries no `last4`. */
export function normalizeCard(body: unknown): CardOnFile | null {
  if (body === null || typeof body !== 'object') return null;
  const raw = body as ResellerBillingCard;
  const last4 = asOptionalString(raw.last4);
  if (last4 === undefined) return null;
  return {
    brand: raw.brand ?? '',
    last4,
    expMonth: raw.expMonth ?? null,
    expYear: raw.expYear ?? null,
    status: asEnum<CardOnFileStatus>(raw.status, CARD_STATUSES),
  };
}

/** A `requires_action` answer's `clientAction`, when its client secret is a string. */
function pendingClientAction(body: unknown): ClientAction | null {
  if (body === null || typeof body !== 'object') return null;
  const raw = body as ResellerBillingCard;
  const action = raw.clientAction;
  if (raw.status !== 'requires_action' || action == null || action.clientSecret === null) {
    return null;
  }
  return action;
}

function normalizeCharge(raw: ResellerCharge): ResellerChargeItem {
  return {
    id: raw.id,
    kind: asEnum<ChargeKind>(raw.kind, CHARGE_KINDS),
    status: asChargeStatus(raw.status),
    amountMinor: raw.amountMinor,
    currency: raw.currency,
    attempt: raw.attempt,
    createdAt: raw.createdAt,
    nextRetryAt: asOptionalString(raw.nextRetryAt) ?? null,
    periodStart: raw.periodStart,
    failureCode: asOptionalString(raw.failureCode) ?? null,
    holdsPayouts: raw.holdsPayouts,
  };
}

function normalizeNoticeCharge(raw: ResellerNoticeCharge): NoticeCharge {
  return {
    currency: raw.currency,
    amountMinor: raw.amountMinor,
    status: asChargeStatus(raw.status),
  };
}

function normalizeNotice(raw: ResellerNoticeView): ResellerNotice {
  const facts = raw.facts;
  return {
    id: raw.id,
    kind: asEnum<NoticeKind>(raw.kind, NOTICE_KINDS),
    status: asEnum<NoticeStatus>(raw.status, NOTICE_STATUSES),
    periodOn: raw.periodOn,
    recipientCount: raw.recipientCount ?? null,
    createdAt: raw.createdAt,
    sentAt: asOptionalString(raw.sentAt) ?? null,
    facts: {
      month: asOptionalString(facts.month) ?? null,
      currency: facts.currency ?? '',
      shortfallMinor: facts.shortfallMinor ?? null,
      probationMonths: facts.probationMonths ?? null,
      stillNeededMinor: facts.stillNeededMinor ?? null,
      deactivationOn: asOptionalString(facts.deactivationOn) ?? null,
      launchOn: asOptionalString(facts.launchOn) ?? null,
      charges: (facts.charges ?? []).map(normalizeNoticeCharge),
    },
  };
}

const CARD = '/v1/reseller-admin/billing/card';
const COLLECTION = '/v1/reseller-admin/billing/collection-authorization';

export const resellerBillingApi = {
  async getCard(): Promise<CardOnFile | null> {
    return normalizeCard(await request<ResellerBillingCard | null>('GET', CARD));
  },

  /** OWNER only. A `requires_action` answer is replayed with the same body after 3-D Secure. */
  async putCard(cardToken: string): Promise<CardPutResult> {
    const body = await request<unknown>('PUT', CARD, {
      json: { cardToken } satisfies CardPutRequest,
    });
    const clientAction = pendingClientAction(body);
    if (clientAction !== null) return { kind: 'requiresAction', clientAction };
    const card = normalizeCard(body);
    if (card !== null) return { kind: 'saved', card };
    throw new ApiError('UNKNOWN', 200, {});
  },

  /** OWNER only. `409 RESELLER_CHARGE_UNPAID` while any charge is unpaid. */
  async deleteCard(): Promise<void> {
    await request<unknown>('DELETE', CARD);
  },

  async listCharges(): Promise<ResellerChargeItem[]> {
    const body = (await request<unknown>(
      'GET',
      '/v1/reseller-admin/billing/charges',
    )) as ResellerChargeList;
    return body.charges.map(normalizeCharge);
  },

  async listNotices(): Promise<ResellerNotice[]> {
    const body = (await request<unknown>('GET', '/v1/reseller-admin/notices')) as ResellerNoticeList;
    return body.notices.map(normalizeNotice);
  },

  getCollectionAuthorization(): Promise<CollectionAuthorizationView> {
    return request<unknown>('GET', COLLECTION).then(normalizeCollection);
  },

  /** OWNER only. `version` is the served `termsVersion`. Sends no card data. */
  authorizeCollection(version: string): Promise<CollectionAuthorizationView> {
    const body = { version, accept: true } satisfies CollectionAuthorizationRequest;
    return request<unknown>('POST', COLLECTION, { json: body }).then(normalizeCollection);
  },

  /** OWNER only. Withdraws collection. Sends no card data. */
  async revokeCollectionAuthorization(): Promise<void> {
    await request<unknown>('DELETE', COLLECTION);
  },
};

const CONCURRENT_UPDATE_RETRIES = 2;
const CONCURRENT_UPDATE_WAIT_MS = 1500;

/**
 * `putCard`, retried after `409 CONCURRENT_UPDATE` (another save of the card is in flight) —
 * at most twice, 1.5 s apart, then rethrown. The server derives the idempotency key, so sending
 * the same body again is safe.
 */
// `409 CONCURRENT_UPDATE` is retryable (TMMAPI-0102 contract).
export async function putCardWithRetry(
  cardToken: string,
  wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
): Promise<CardPutResult> {
  for (let retries = 0; ; retries += 1) {
    try {
      return await resellerBillingApi.putCard(cardToken);
    } catch (err) {
      if (backendCode(err) !== 'CONCURRENT_UPDATE' || retries >= CONCURRENT_UPDATE_RETRIES) {
        throw err;
      }
      await wait(CONCURRENT_UPDATE_WAIT_MS);
    }
  }
}
